import React from "react";
import {
  AbsoluteFill,
  Img,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
  Easing,
} from "remotion";

/*
 * Kyu Store — Esports Broadcast Mode
 * Belajar dari: Charleroi Esports (3 cut/detik, kilat strobo ungu, tipografi bleed)
 *               BREN Esports (energy ramp cenderung emas -> payoff logo)
 * Runtime: 360 frames @ 30fps = 12s. 1080x1920.
 */

const PURPLE = "#5D3FD3";
const PURPLE_DEEP = "#1E124A";
const PURPLE_DARK = "#120A2E";
const GOLD = "#FFB020";
const GOLD_HOT = "#FFD166";
const MAGENTA = "#C026D3";
const WHITE = "#FFFFFF";

const EASE = Easing.bezier(0.16, 1, 0.3, 1);

// ---------- helpers ----------
const flashAt = (frame: number, cuts: number[], len = 3, power = 1) => {
  for (const c of cuts) {
    if (frame >= c && frame < c + len) {
      return interpolate(frame, [c, c + len], [power, 0], { extrapolateRight: "clamp" });
    }
  }
  return 0;
};

// giant condensed uppercase word, bleeding crop khas esports
const BleedWord: React.FC<{
  text: string;
  color?: string;
  stroke?: boolean;
  size?: number;
  offsetY?: number;
  opacity?: number;
  scaleX?: number;
}> = ({ text, color = WHITE, stroke = false, size = 300, offsetY = 0, opacity = 1, scaleX = 0.82 }) => (
  <div
    style={{
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: `translate(-50%, calc(-50% + ${offsetY}px)) scaleX(${scaleX})`,
      fontSize: size,
      fontWeight: 900,
      letterSpacing: "-0.02em",
      lineHeight: 0.95,
      whiteSpace: "nowrap",
      textTransform: "uppercase",
      color: stroke ? "transparent" : color,
      WebkitTextStroke: stroke ? `3px ${color}` : undefined,
      opacity,
      userSelect: "none",
    }}
  >
    {text}
  </div>
);

export const KyuStoreEsportsBroadcast: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const sp = (delay: number, damping = 13, stiffness = 140) =>
    spring({ frame: frame - delay, fps, config: { damping, stiffness, mass: 1 } });

  // ============================================================
  // BABAK 1 — HYPER CUT OPENING (0-120): ~3 cut/detik + strobo
  // ============================================================
  // potongan tiap 10-12 frame = 2.5-3 cut per detik
  const CUTS = [0, 10, 21, 31, 42, 52, 63, 73, 84, 94, 105];
  const cutIndex = CUTS.filter((c) => frame >= c).length - 1; // -1..10

  const WORDS = [
    { text: "TOP UP", sub: "MOBILE LEGENDS" },
    { text: "SPEED", sub: "0.8 DETIK MASUK" },
    { text: "HARGA", sub: "MURAH SYERR" },
    { text: "SLOT", sub: "READY 24 JAM" },
    { text: "KYU", sub: "STORE" },
    { text: "NO", sub: "SCAM • NO HIDDEN" },
    { text: "QRIS", sub: "SEMUA BANK" },
    { text: "DIAMOND", sub: "RESI OTOMATIS" },
    { text: "GAS", sub: "TOP UP SEKARANG" },
    { text: "KYUSTORE", sub: "•COM" },
  ];

  // frame relatif dalam potongan + posisi potongan
  const segStart = cutIndex >= 0 ? CUTS[Math.min(cutIndex, CUTS.length - 1)] : 0;
  const segLen = (CUTS[cutIndex + 1] ?? 120) - segStart;
  const segT = frame - segStart; // 0..segLen
  const segProg = segT / Math.max(segLen, 1);

  // word entry: slide + scale cepat (easeOutExpo), keluar dengan cepat
  const wordIn = interpolate(segT, [0, 4], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: EASE });
  const wordOut = interpolate(segProg, [0.7, 1], [1, 0.92], { extrapolateLeft: "clamp" });
  const wordSlide = interpolate(wordIn, [0, 1], [70, 0]);
  const wordX = (cutIndex % 3) - 1; // posisi variasi: kiri/tengah/kanan

  // strobo flash tiap potongan (kilat magenta / emas / ungu)
  const FLASH_COLORS = [MAGENTA, GOLD, PURPLE, MAGENTA, GOLD_HOT, PURPLE, MAGENTA, GOLD, PURPLE, GOLD_HOT];
  const flash = flashAt(frame, CUTS, 3, 0.85);
  const flashColor = FLASH_COLORS[cutIndex % FLASH_COLORS.length];

  // ghost outline text raksasa di belakang (bergerak konstan pelan)
  const ghostX = interpolate(frame, [0, 120], [0, -260]);
  const ghostY = Math.sin(frame / 14) * 10;

  // diagonal speed streaks
  const streakShift = (frame * 14) % 420;

  // ============================================================
  // TRANSISI MOSAIC (frame 116-140)
  // ============================================================
  const mosaicT = interpolate(frame, [116, 142], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: EASE });
  const showMosaic = frame >= 116 && frame < 150;

  // ============================================================
  // BABAK 2 — HUD TOURNAMENT CARD (130-272)
  // ============================================================
  const hudIn = sp(132, 13, 120);
  const hudY = interpolate(hudIn, [0, 1], [80, 0]);
  const hudScale = interpolate(hudIn, [0, 1], [0.88, 1]);
  const hudOpacity = interpolate(hudIn, [0, 0.6], [0, 1]);
  const hudExit = interpolate(frame, [258, 280], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: EASE });
  const hudOutY = interpolate(hudExit, [0, 1], [0, -60]);
  const hudOutOpacity = interpolate(hudExit, [0, 0.8], [1, 0]);

  // stagger kartu paket (3 kartu)
  const cardSp = (i: number) => sp(148 + i * 4, 13, 130);

  // ticker bawah berjalan
  const tickerX = -((frame * 6) % 1400);
  // scanline bergeser
  const scanY = (frame * 2.2) % 8;

  // diamond glow pulse
  const glowPulse = 0.65 + Math.sin(frame / 7) * 0.35;

  // ============================================================
  // BABAK 3 — ENERGY RAMPOK + PAYOFF (272-360)
  // ============================================================
  const rampT = interpolate(frame, [268, 340], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.bezier(0.4, 0, 0.6, 1) });
  // kecerahan & saturasi naik terus (aliran BREN)
  const rampBright = interpolate(rampT, [0, 1], [1, 1.55]);
  const rampSat = interpolate(rampT, [0, 1], [1, 1.6]);

  const logoSp = sp(322, 12, 130);
  const logoScale = interpolate(logoSp, [0, 1], [1.5, 1]);
  const logoGlow = interpolate(rampT, [0.6, 1], [0, 1], { extrapolateLeft: "clamp" });

  // kilat putih transisi payoff
  const payoffFlash = flashAt(frame, [318], 6, 1);
  const endFade = interpolate(frame, [344, 360], [1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

  // ============================================================
  // PACKAGES DATA
  // ============================================================
  const PACKS = [
    { name: "WEEKLY PASS", price: "27.500", tag: "BEST SELLER", glow: PURPLE },
    { name: "86 DIAMOND", price: "21.000", tag: "POPULER", glow: MAGENTA },
    { name: "257 DIAMOND", price: "59.000", tag: "BEST VALUE", glow: GOLD },
  ];

  return (
    <AbsoluteFill
      style={{
        backgroundColor: PURPLE_DARK,
        fontFamily: "-apple-system, 'Arial Black', 'Helvetica Neue', sans-serif",
        color: WHITE,
        overflow: "hidden",
      }}
    >
      {/* ================= GLOBAL BACKDROP: radial ungu + grid ================= */}
      <AbsoluteFill
        style={{
          background: `radial-gradient(circle at 50% 42%, #2A1560 0%, ${PURPLE_DARK} 62%)`,
        }}
      />
      {/* halftone dots ungu tipis */}
      <AbsoluteFill
        style={{
          backgroundImage: "radial-gradient(rgba(139,92,246,0.16) 1.5px, transparent 1.5px)",
          backgroundSize: "34px 34px",
        }}
      />

      {/* ================= BABAK 1: HYPER CUT (0-120) ================= */}
      {frame < 126 && (
        <AbsoluteFill>
          {/* ghost outline text raksasa */}
          <div style={{ position: "absolute", top: "38%", left: ghostX - 200, transform: `translateY(${ghostY}px) scaleX(0.85)` }}>
            <div
              style={{
                fontSize: 420,
                fontWeight: 900,
                color: "transparent",
                WebkitTextStroke: "4px rgba(139,92,246,0.28)",
                whiteSpace: "nowrap",
                letterSpacing: "-0.03em",
              }}
            >
              KYUSTORE KYUSTORE
            </div>
          </div>

          {/* speed streaks diagonal */}
          {[0, 1, 2, 3, 4].map((i) => (
            <div
              key={i}
              style={{
                position: "absolute",
                top: -200 + i * 420 + ((streakShift + i * 60) % 200),
                left: -300,
                width: 1700,
                height: 3,
                background: `linear-gradient(90deg, transparent, ${i % 2 ? "rgba(255,176,32,0.35)" : "rgba(192,38,211,0.35)"}, transparent)`,
                transform: "rotate(-18deg)",
              }}
            />
          ))}

          {/* KATA UTAMA per potongan */}
          {cutIndex >= 0 && cutIndex < WORDS.length && (
            <div
              style={{
                position: "absolute",
                top: "46%",
                left: "50%",
                transform: `translate(calc(-50% + ${wordX * 90}px), calc(-50% + ${wordSlide}px)) scaleX(0.8)`,
                textAlign: "center",
                opacity: wordOut,
              }}
            >
              <div
                style={{
                  fontSize: 250,
                  fontWeight: 900,
                  letterSpacing: "-0.02em",
                  lineHeight: 0.92,
                  color: cutIndex % 4 === 2 ? GOLD : WHITE,
                  textShadow: "0 0 55px rgba(139,92,246,0.8), 0 12px 40px rgba(0,0,0,0.6)",
                }}
              >
                {WORDS[cutIndex].text}
              </div>
              <div
                style={{
                  fontSize: 40,
                  fontWeight: 900,
                  letterSpacing: "0.42em",
                  color: cutIndex % 2 ? GOLD_HOT : "rgba(255,255,255,0.85)",
                  marginTop: 18,
                }}
              >
                {WORDS[cutIndex].sub}
              </div>
            </div>
          )}

          {/* HUD micro-labels sudut (konstan dari detik 0) */}
          <div style={{ position: "absolute", top: 80, left: 80, fontSize: 22, fontWeight: 900, letterSpacing: "0.3em", color: "rgba(255,255,255,0.75)" }}>
            [ KYU // OFFICIAL STORE ]
          </div>
          <div style={{ position: "absolute", top: 80, right: 80, display: "flex", alignItems: "center", gap: 10, fontSize: 22, fontWeight: 900, letterSpacing: "0.2em", color: GOLD_HOT }}>
            <div style={{ width: 10, height: 10, borderRadius: "50%", background: GOLD, boxShadow: `0 0 12px ${GOLD}` }} />
            LIVE INJECT
          </div>
        </AbsoluteFill>
      )}

      {/* ================= MOSAIC TRANSITION (116-150) ================= */}
      {showMosaic &&
        Array.from({ length: 8 * 14 }).map((_, i) => {
          const col = i % 8;
          const row = Math.floor(i / 8);
          // blok muncul acak berdasarkan frame
          const thresh = ((col * 7 + row * 13) % 29) / 29;
          const on = mosaicT > thresh && mosaicT < thresh + 0.42;
          return (
            <div
              key={i}
              style={{
                position: "absolute",
                left: col * 135,
                top: row * 138,
                width: 136,
                height: 139,
                background: (col + row) % 3 === 0 ? GOLD : (col + row) % 3 === 1 ? MAGENTA : PURPLE,
                opacity: on ? 0.92 : 0,
              }}
            />
          );
        })}

      {/* ================= BABAK 2: HUD TOURNAMENT (130-280) ================= */}
      {frame >= 126 && frame < 286 && (
        <AbsoluteFill style={{ opacity: hudOpacity * hudOutOpacity }}>
          <div
            style={{
              position: "absolute",
              top: "46%",
              left: "50%",
              transform: `translate(-50%, calc(-50% + ${hudY + hudOutY}px)) scale(${hudScale})`,
              width: 940,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {/* bracket sudut gaya tournament HUD */}
            {[
              { top: -34, left: -34, borderTop: true, borderLeft: true },
              { top: -34, right: -34, borderTop: true, borderRight: true },
              { bottom: -34, left: -34, borderBottom: true, borderLeft: true },
              { bottom: -34, right: -34, borderBottom: true, borderRight: true },
            ].map((s, i) => (
              <div
                key={i}
                style={{
                  position: "absolute",
                  width: 64,
                  height: 64,
                  borderTop: s.borderTop ? `6px solid ${GOLD}` : undefined,
                  borderBottom: s.borderBottom ? `6px solid ${GOLD}` : undefined,
                  borderLeft: s.borderLeft ? `6px solid ${GOLD}` : undefined,
                  borderRight: s.borderRight ? `6px solid ${GOLD}` : undefined,
                  opacity: hudIn,
                }}
              />
            ))}

            {/* headline */}
            <div style={{ fontSize: 66, fontWeight: 900, letterSpacing: "-0.01em", textAlign: "center", lineHeight: 1.02, transform: "scaleX(0.88)" }}>
              PILIH DIAMOND <span style={{ color: GOLD_HOT }}>KAMU</span>
            </div>
            <div style={{ fontSize: 24, fontWeight: 900, letterSpacing: "0.4em", color: "rgba(255,255,255,0.6)", marginTop: 16 }}>
              [ AUTO INJECT • 0.8 DETIK • 24 JAM ]
            </div>

            {/* 3 KARTU PAKET */}
            <div style={{ display: "flex", gap: 22, marginTop: 52 }}>
              {PACKS.map((p, i) => {
                const s = cardSp(i);
                const y = interpolate(s, [0, 1], [70, 0]);
                const sc = interpolate(s, [0, 1], [0.9, 1]);
                return (
                  <div
                    key={p.name}
                    style={{
                      width: 288,
                      backgroundColor: "rgba(255,255,255,0.055)",
                      border: `2.5px solid ${i === 0 ? p.glow : "rgba(255,255,255,0.22)"}`,
                      borderRadius: 26,
                      padding: "30px 24px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      transform: `translateY(${y}px) scale(${sc})`,
                      opacity: s,
                      boxShadow: i === 0 ? `0 0 45px ${p.glow}66, inset 0 0 30px ${p.glow}22` : "0 14px 35px rgba(0,0,0,0.35)",
                      position: "relative",
                    }}
                  >
                    {/* tag */}
                    <div
                      style={{
                        position: "absolute",
                        top: -16,
                        background: i === 2 ? GOLD : p.glow,
                        color: i === 2 ? PURPLE_DARK : WHITE,
                        fontSize: 15,
                        fontWeight: 900,
                        letterSpacing: "0.14em",
                        padding: "6px 16px",
                        borderRadius: 12,
                      }}
                    >
                      {p.tag}
                    </div>

                    {/* diamond glow */}
                    <svg width="72" height="72" viewBox="0 0 100 100" style={{ filter: `drop-shadow(0 0 ${14 * glowPulse}px ${p.glow})`, marginTop: 8 }}>
                      <defs>
                        <linearGradient id={`dg${i}`} x1="0" y1="0" x2="1" y2="1">
                          <stop offset="0%" stopColor="#FFFFFF" />
                          <stop offset="100%" stopColor={p.glow} />
                        </linearGradient>
                      </defs>
                      <polygon points="26,16 74,16 88,38 12,38" fill={`url(#dg${i})`} />
                      <polygon points="12,38 50,38 50,92" fill={p.glow} opacity="0.9" />
                      <polygon points="50,38 88,38 50,92" fill={p.glow} opacity="0.6" />
                      <polygon points="2,38 12,38 50,92" fill="#FFFFFF" opacity="0.35" />
                      <polygon points="26,16 50,38 74,16" fill="#FFFFFF" opacity="0.85" />
                    </svg>

                    <div style={{ fontSize: 26, fontWeight: 900, marginTop: 16, letterSpacing: "0.01em" }}>{p.name}</div>
                    <div style={{ fontSize: 40, fontWeight: 900, color: i === 0 ? GOLD_HOT : WHITE, marginTop: 10 }}>
                      Rp {p.price}
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 800, letterSpacing: "0.2em", color: "rgba(255,255,255,0.55)", marginTop: 10 }}>
                      USER ID ONLY
                    </div>
                  </div>
                );
              })}
            </div>

            {/* ticker bawah */}
            <div
              style={{
                marginTop: 60,
                width: 940,
                overflow: "hidden",
                borderTop: "2px solid rgba(255,255,255,0.14)",
                borderBottom: "2px solid rgba(255,255,255,0.14)",
                padding: "12px 0",
              }}
            >
              <div style={{ display: "flex", transform: `translateX(${tickerX}px)`, whiteSpace: "nowrap" }}>
                {Array.from({ length: 4 }).map((_, i) => (
                  <span key={i} style={{ fontSize: 21, fontWeight: 900, letterSpacing: "0.26em", color: "rgba(255,255,255,0.55)", paddingRight: 60 }}>
                    QRIS ★ BCA ★ MANDIRI ★ BRI ★ DANA ★ GOPAY ★ SHOPEEPAY ★ VERIFIED SELLER ★
                  </span>
                ))}
              </div>
            </div>
          </div>
        </AbsoluteFill>
      )}

      {/* ================= BABAK 3: ENERGY RAMPOK + PAYOFF (268-360) ================= */}
      {frame >= 262 && (
        <AbsoluteFill>
          {/* ghost text besar naik */}
          <div
            style={{
              position: "absolute",
              top: "30%",
              left: "50%",
              transform: `translateX(-50%) scaleX(0.85) translateY(${interpolate(frame, [262, 360], [60, -60])}px)`,
              fontSize: 340,
              fontWeight: 900,
              color: "transparent",
              WebkitTextStroke: `5px rgba(255,176,32,${0.1 + rampT * 0.22})`,
              whiteSpace: "nowrap",
            }}
          >
            TOP UP NOW
          </div>

          {/* beam emas menyala dari tengah */}
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: interpolate(rampT, [0, 1], [200, 1500]),
              height: interpolate(rampT, [0, 1], [200, 1500]),
              background: `radial-gradient(circle, rgba(255,209,102,${0.16 + rampT * 0.3}) 0%, rgba(93,63,211,${0.12 * (1 - rampT)}) 45%, transparent 70%)`,
              borderRadius: "50%",
            }}
          />

          {/* LOGO PAYOFF */}
          {frame >= 300 && (
            <div
              style={{
                position: "absolute",
                top: "47%",
                left: "50%",
                transform: `translate(-50%, -50%) scale(${logoScale})`,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                opacity: interpolate(frame, [300, 312], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" }),
              }}
            >
              <Img
                src={staticFile("kyu_store_logo.png")}
                style={{
                  height: 210,
                  objectFit: "contain",
                  filter: `drop-shadow(0 0 ${30 + logoGlow * 60}px rgba(255,176,32,${0.35 + logoGlow * 0.5})) drop-shadow(0 0 ${80 * logoGlow}px rgba(93,63,211,0.6))`,
                }}
              />
              <div style={{ fontSize: 54, fontWeight: 900, letterSpacing: "0.14em", marginTop: 30, transform: "scaleX(0.9)", textShadow: "0 0 40px rgba(255,209,102,0.6)" }}>
                KYUSTORE.COM
              </div>
              <div style={{ fontSize: 23, fontWeight: 900, letterSpacing: "0.42em", color: "rgba(255,255,255,0.75)", marginTop: 18 }}>
                TOP UP SATSET • ANTI RIBET
              </div>
            </div>
          )}
        </AbsoluteFill>
      )}

      {/* ================= SCANLINE GLOBAL ================= */}
      <AbsoluteFill
        style={{
          backgroundImage: "repeating-linear-gradient(0deg, rgba(0,0,0,0.16) 0px, rgba(0,0,0,0.16) 1.5px, transparent 1.5px, transparent 6px)",
          backgroundPosition: `0 ${scanY}px`,
          pointerEvents: "none",
          opacity: 0.55,
        }}
      />

      {/* ================= VIGNETTE ================= */}
      <AbsoluteFill
        style={{
          background: "radial-gradient(ellipse at center, transparent 42%, rgba(0,0,0,0.55) 100%)",
          pointerEvents: "none",
        }}
      />

      {/* ================= KILAT STROBO (overlay warna per potongan) ================= */}
      {flash > 0 && (
        <AbsoluteFill style={{ background: flashColor, opacity: flash * 0.55, mixBlendMode: "screen", pointerEvents: "none" }} />
      )}

      {/* ================= KILAT PAYOFF PUTIH ================= */}
      {payoffFlash > 0 && <AbsoluteFill style={{ background: WHITE, opacity: payoffFlash * 0.9, pointerEvents: "none" }} />}

      {/* ================= END FADE ================= */}
      {frame >= 344 && <AbsoluteFill style={{ background: PURPLE_DARK, opacity: 1 - endFade, pointerEvents: "none" }} />}

      {/* ================= FILTER RAMPOK (brightness/saturasi naik) ================= */}
      <AbsoluteFill
        style={{
          backdropFilter: `brightness(${rampBright}) saturate(${rampSat})`,
          pointerEvents: "none",
          opacity: rampT > 0 ? 1 : 0,
        }}
      />
    </AbsoluteFill>
  );
};
